import React, { useRef } from 'react';
import { Button } from '@mui/material';
import PopoverComponent, { PopoverComponentRef } from 'src/components/Popover';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import useYupValidationResolver from 'src/hooks/useYupValidationResolver';
import toast from 'react-hot-toast';
import { confirmWrapper } from 'src/components/Dialog';
import { SeperatorIcon } from 'src/icons';
import RHFInput from 'src/components/elements/Input/RHFInput';
import { Confirmation } from 'src/components';
import * as yup from 'yup';
import { DialogContentProps } from 'src/components/Dialog/Dialog';
import { CurrencyFormSubmitHandler, CurrencyFormValues } from '../../types';

interface AddCurrencyProps extends DialogContentProps {
  defaultValues?: CurrencyFormValues;
  onCreateCurrency: (data: CurrencyFormSubmitHandler) => void;
}

const validationSchema = yup.object().shape({
  code: yup.string().typeError('Enter currency code').required('Code required'),
  symbol: yup.string().typeError('Enter currency symbol').required('Symbol required'),
  name: yup.string().optional(),
  sortOrder: yup.number()
    .transform((value) => (Number.isNaN(value) ? undefined : value))
    .optional(),
});

const AddCurrency = function AddCurrency(props: AddCurrencyProps) {
  const {
    defaultValues,
    hideDialogLoading,
    showDialogLoading,
    close,
    onCreateCurrency,
  } = props;

  const approveRef = useRef<PopoverComponentRef>(null);
  const resolver = useYupValidationResolver(validationSchema);
  const methods = useForm<CurrencyFormValues>({
    defaultValues,
    resolver,
  });

  const {
    handleSubmit, reset,
    formState: { isDirty },
  } = methods;
  const { id } = defaultValues || {};

  const showLoading = (message: string) => {
    if (showDialogLoading) {
      showDialogLoading(message);
    }
  };

  const hideLoading = () => {
    if (hideDialogLoading) {
      hideDialogLoading();
    }
  };

  const submitHandler: SubmitHandler<CurrencyFormValues> = async () => {
    if (Object.keys(methods.formState.dirtyFields).length > 0) {
      if (approveRef.current?.open) {
        approveRef.current?.open();
      }
    } else {
      toast('No changes', {
        position: 'bottom-center',
      });
    }
  };

  const handleCreate = async () => {
    const data = methods.getValues();
    try {
      if (Object.keys(methods.formState.dirtyFields).length > 0) {
        const loadingMessage = id ? 'Updating Currency...' : 'Creating New Currency ...';
        showLoading(loadingMessage);
        const userFormObj: CurrencyFormSubmitHandler = {
          values: data,
          formInstance: methods,
        };
        await onCreateCurrency(userFormObj);
        if (close) {
          close();
          const message = id ? 'Currency updated successfully!' : 'Currency created successfully!';
          await confirmWrapper(message, {
            hideSecondaryBtn: true,
            hidePrimaryBtn: true,
            variant: 'success',
            autoClose: true,
          });
        }
      } else {
        toast('No changes', {
          position: 'bottom-center',
        });
      }
    } catch (e) {
      if (approveRef.current?.close) approveRef.current?.close();
    } finally {
      hideLoading();
    }
  };

  const confirmationMessage = id
    ? 'Are you sure you want to update the currency?'
    : 'Are you sure you want to add new currency?';

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(submitHandler)}>
        <div className="flex justify-center items-center w-full h-[60px] py-10">
          <SeperatorIcon stopColor="#D9D9D9" className="rotate-90" width={90} />
          <div className="flex justify-center text-[#28364C] font-medium text-base pl-[13px]">
            {id ? 'Edit Currency' : 'Add New Currency'}
          </div>
          <SeperatorIcon stopColor="#D9D9D9" className="rotate-90" width={90} />
        </div>

        <div className="flex items-start justify-center px-[10%] gap-7 bg-[#FAFAFA] py-[5%] flex-wrap">
          <div className="w-[180px]">
            <RHFInput name="code" control={methods.control} label="Code" />
          </div>
          <div className="w-[180px]">
            <RHFInput name="symbol" control={methods.control} label="Symbol" />
          </div>
          <div className="w-[180px]">
            <RHFInput name="name" control={methods.control} label="Name" />
          </div>
          <div className="w-[180px]">
            <RHFInput name="sortOrder" control={methods.control} label="Sort Order" type="number" />
          </div>
        </div>

        <div className="flex justify-center items-center py-[20px] gap-4">
          <Button
            className="w-[114px]"
            variant="outlined"
            disabled={!isDirty}
            onClick={() => reset({})}
          >
            Reset
          </Button>
          <PopoverComponent
            ref={approveRef}
            controlled
            Component={(
              <Confirmation
                variant="warning"
                text={confirmationMessage}
                confirm={handleCreate}
              />
            )}
          >
            <Button
              className="w-[114px]"
              type="submit"
              color="primary"
              variant="contained"
            >
              {id ? 'Update' : 'Add'}
            </Button>
          </PopoverComponent>
        </div>
      </form>
    </FormProvider>
  );
};

AddCurrency.defaultProps = {
  defaultValues: undefined,
};

export default AddCurrency;
